import React, { useState, useEffect } from "react";
import { Wrench, Loader2, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { PageHeader, Card, SandboxTag } from "../components/common";
import { toast } from "sonner";

export default function AutoFix() {
  const { t } = useI18n();
  const [issues, setIssues] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState("");

  const scan = async () => {
    setScanning(true);
    try { const { data } = await api.get("/autofix/scan"); setIssues(data.issues); }
    finally { setScanning(false); }
  };
  useEffect(() => { scan(); }, []);

  const apply = async (issue) => {
    setApplying(issue.id);
    try {
      const { data } = await api.post("/autofix/apply", { id: issue.id });
      toast.success(`Auto-fix applied to ${data.applied} record(s) — audit event created`);
      scan();
    } catch (e) { toast.error("Auto-fix failed"); }
    finally { setApplying(""); }
  };

  const STEPS = ["Detect", "Explain", "Recommend", "Preview", "Apply", "Validate", "Audit"];

  return (
    <div>
      <PageHeader title={t("nav.autofix")} subtitle="Diagnose and repair sandbox workflow issues — never modifies real systems" summary="AI Assist and Auto-Fix. Detects sandbox workflow issues like missing owners, stuck approvals and incomplete findings, then applies safe fixes with audit trails.">
        <SandboxTag text="SANDBOX AUTO-FIX ONLY" />
      </PageHeader>

      <Card title="Auto-Fix Workflow" className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          {STEPS.map((s, i) => <React.Fragment key={s}><span className="rounded-full bg-[var(--qatar-subtle)] px-3 py-1 text-xs font-semibold text-[var(--qatar)]">{s}</span>{i < STEPS.length - 1 && <span className="text-slate-300">→</span>}</React.Fragment>)}
        </div>
      </Card>

      <div className="mb-4 flex justify-end">
        <button data-testid="autofix-scan" onClick={scan} disabled={scanning} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50">
          {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />} {t("scan")}
        </button>
      </div>

      <div className="space-y-3">
        {(issues || []).map((issue) => (
          <div key={issue.id} data-testid={`autofix-${issue.id}`} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 card-shadow md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              {issue.id === "FIX-000" ? <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-500" /> : <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-500" />}
              <div>
                <p className="text-sm font-semibold text-slate-800">{issue.type} {issue.count > 0 && <span className="text-slate-400">· {issue.count} record(s)</span>}</p>
                <p className="text-xs text-slate-500">{issue.detail}</p>
                <p className="mt-1 text-xs text-[var(--qatar)]">Recommended fix: {issue.fix}</p>
              </div>
            </div>
            {issue.id !== "FIX-000" && (
              <button data-testid={`autofix-apply-${issue.id}`} onClick={() => apply(issue)} disabled={applying === issue.id} className="qatar-gradient inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {applying === issue.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("apply_fix")}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
